import { z } from "zod";

import {
  TIME_RANGES,
  type PresetRange,
} from "@/shared/domain/dashboards/DashboardDefinition";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";

import {
  instantOfWallClock,
  wallClockMs,
  type BoardCalendar,
} from "./boardCalendar";
import { boardWallClockText, boardZoneText } from "./boardTimeText";

/**
 * The forms a board's `time_range` takes in its definition, and what each one
 * means: a preset such as `"90d"` (the presets a metrics dashboard offers), a
 * custom range whose bounds are dates or times read in the board's time zone,
 * and a relative range. The board feature and the chat's board tools both
 * read this module, so the definition schema, the board's time range editor
 * and the tools' confirmation cards agree on which time ranges exist and what
 * they are called.
 */

/** The units a relative range counts in, shortest first. */
export const RELATIVE_RANGE_UNITS = [
  "hours",
  "days",
  "weeks",
  "months",
  "years",
] as const;

export type RelativeRangeUnit = (typeof RELATIVE_RANGE_UNITS)[number];

/** The largest `amount` a relative range takes. */
export const RELATIVE_RANGE_MAX_AMOUNT = 9999;

/**
 * The most recent `amount` units, ending when the board resolves it
 * (`resolveBoardTimeRange`). Like a preset it is stored as written and
 * resolved each time the board opens, so a saved "last 7 days" keeps meaning
 * the last 7 days.
 */
export interface RelativeRange {
  kind: "relative";
  /** A whole number from 1 to `RELATIVE_RANGE_MAX_AMOUNT`. */
  amount: number;
  unit: RelativeRangeUnit;
}

/**
 * A range between two bounds, each a date (`2026-09-06`) or a date and time
 * (`2026-09-06T14:00`, `2026-09-06T14:00:30`, `2026-09-06T14:00:30.250`)
 * written with no zone and read in the board's time zone. A date at the
 * start begins at that day's midnight; a date at the end includes that whole
 * day; a time is that instant, and at the end the range stops just before
 * it.
 */
export interface CustomBoardTimeRange {
  kind: "custom";
  start: string;
  end: string;
}

/** A board's time range: a preset, a custom range, or a relative range. */
export type BoardTimeRangeMode =
  | PresetRange
  | CustomBoardTimeRange
  | RelativeRange;

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

/** How long one unit is. A month is 30 days and a year 365, the lengths the
 *  presets use: `90d` is labeled 3M and `365d` is labeled 1Y. */
const UNIT_MS: Record<RelativeRangeUnit, number> = {
  hours: MS_PER_HOUR,
  days: MS_PER_DAY,
  weeks: 7 * MS_PER_DAY,
  months: 30 * MS_PER_DAY,
  years: 365 * MS_PER_DAY,
};

const UNIT_SINGULAR: Record<RelativeRangeUnit, string> = {
  hours: "hour",
  days: "day",
  weeks: "week",
  months: "month",
  years: "year",
};

/** A custom bound: a date, and optionally a time of day to the millisecond,
 *  with no zone. */
const CUSTOM_BOUND =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/;

/** A date and time followed by `Z` or an offset, which a custom bound does
 *  not take: the board's time zone decides what its times mean. */
const BOUND_WITH_ZONE =
  /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

/** One bound of a custom range as the board reads it: the wall-clock time it
 *  names, written as milliseconds as if that time were UTC, and whether it
 *  names a whole day. */
interface CustomBound {
  wallMs: number;
  isDate: boolean;
}

/** The bound `text` names, or null when it is not a real date, or a real date
 *  and time of day, in a form a custom bound takes. */
function customBoundOf(text: string): CustomBound | null {
  const match = CUSTOM_BOUND.exec(text);
  if (match === null) {
    return null;
  }
  const [year, month, day, hours, minutes, seconds] = match
    .slice(1, 7)
    .map((part) => Number(part ?? 0));
  const milliseconds = Number((match[7] ?? "").padEnd(3, "0"));
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(hours, minutes, seconds, milliseconds);
  const real =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCHours() === hours &&
    date.getUTCMinutes() === minutes &&
    date.getUTCSeconds() === seconds;
  return real
    ? { wallMs: date.getTime(), isDate: match[4] === undefined }
    : null;
}

/** The wall-clock time just past the range's end: the next day's midnight
 *  for a date, the time itself for a time. */
function endWallMs(end: CustomBound): number {
  return end.isDate ? end.wallMs + MS_PER_DAY : end.wallMs;
}

/** Message for a custom bound written with `Z` or an offset. */
export const CUSTOM_BOUND_WITH_ZONE_MESSAGE =
  'A custom time range\'s times are read in the board\'s time zone, so write them without "Z" or an offset, such as "2026-09-06T14:00".';

const relativeRangeSchema = z.strictObject({
  kind: z.literal("relative"),
  amount: z.int().min(1).max(RELATIVE_RANGE_MAX_AMOUNT),
  unit: z.enum(RELATIVE_RANGE_UNITS),
});

/** A custom range: each bound one `customBoundOf` reads, and the start
 *  before the range's end (`endWallMs`). An unknown key is dropped, as the
 *  Metrics dashboard's custom range drops one. */
const customRangeSchema = z
  .object({
    kind: z.literal("custom"),
    start: z.string(),
    end: z.string(),
  })
  .refine((range) => {
    const start = customBoundOf(range.start);
    const end = customBoundOf(range.end);
    return start !== null && end !== null && start.wallMs < endWallMs(end);
  });

const presetRangeSchema = z.enum(TIME_RANGES.map((preset) => preset.value));

const boardTimeRangeModeSchema = z.union([
  presetRangeSchema,
  customRangeSchema,
  relativeRangeSchema,
]);

/** The preset strings, quoted and comma-separated, for messages that list
 *  what `time_range` accepts. */
export const PRESET_RANGE_LIST = TIME_RANGES.map(
  (preset) => `"${preset.value}"`,
).join(", ");

/** The unit strings, quoted and comma-separated, for the same messages. */
export const RELATIVE_RANGE_UNIT_LIST = RELATIVE_RANGE_UNITS.map(
  (unit) => `"${unit}"`,
).join(", ");

export function isPresetBoardTimeRange(
  mode: BoardTimeRangeMode,
): mode is PresetRange {
  return typeof mode === "string";
}

export function isCustomBoardTimeRange(
  mode: BoardTimeRangeMode,
): mode is CustomBoardTimeRange {
  return typeof mode !== "string" && mode.kind === "custom";
}

export function isRelativeBoardTimeRange(
  mode: BoardTimeRangeMode,
): mode is RelativeRange {
  return typeof mode !== "string" && mode.kind === "relative";
}

/** The board time range a value read from outside the app names (a stored
 *  definition's `time_range`, a tool call's argument), or null when it is
 *  none of the three forms. */
export function asBoardTimeRangeMode(
  value: unknown,
): BoardTimeRangeMode | null {
  const parsed = boardTimeRangeModeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Whether `value` is a custom range with a bound written with `Z` or an
 *  offset, which the definition refuses with its own message. */
export function hasCustomBoundWithZone(value: unknown): boolean {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const { kind, start, end } = value as Record<string, unknown>;
  return (
    kind === "custom" &&
    [start, end].some(
      (bound) => typeof bound === "string" && BOUND_WITH_ZONE.test(bound),
    )
  );
}

/**
 * The instants a board time range covers at `now`, in the board's calendar.
 * A relative range ends at `now` with its seconds dropped and starts
 * `amount` units earlier, which is how a preset resolves
 * (`TimeRange.fromPreset`), so two resolutions within one minute give equal
 * bounds and their queries share cache entries. A custom range's bounds are
 * read in the calendar's zone, so a range of dates covers whole days there.
 */
export function resolveBoardTimeRange(
  mode: BoardTimeRangeMode,
  calendar: BoardCalendar,
  now: Date = new Date(),
): TimeRange {
  if (isPresetBoardTimeRange(mode)) {
    return TimeRange.fromPreset(mode, now);
  }
  if (isRelativeBoardTimeRange(mode)) {
    const end = Math.floor(now.getTime() / MS_PER_MINUTE) * MS_PER_MINUTE;
    return TimeRange.fromMs(end - mode.amount * UNIT_MS[mode.unit], end);
  }
  const start = customBoundOf(mode.start);
  const end = customBoundOf(mode.end);
  if (start === null || end === null) {
    throw new Error(
      `A custom time range needs dates or times, got ${mode.start}/${mode.end}`,
    );
  }
  return TimeRange.fromMs(
    instantOfWallClock(calendar.timeZone, start.wallMs),
    instantOfWallClock(calendar.timeZone, endWallMs(end)),
  );
}

/** Writes the wall-clock time `wallMs` (as `wallClockMs` writes it) as a
 *  custom bound: a date and time, with seconds only when they or the
 *  milliseconds are not zero, and milliseconds only when they are not. */
function customBoundText(wallMs: number): string {
  const iso = new Date(wallMs).toISOString();
  const minutes = iso.slice(0, 16);
  const seconds = iso.slice(17, 19);
  const milliseconds = iso.slice(20, 23);
  if (milliseconds !== "000") {
    return `${minutes}:${seconds}.${milliseconds}`;
  }
  return seconds === "00" ? minutes : `${minutes}:${seconds}`;
}

/**
 * The custom range that covers exactly `timeRange`'s instants in the
 * calendar's zone. A bound at midnight there is written as a date (the end as
 * the day before it, which the range includes), and any other bound as a time
 * to the millisecond, so resolving the range gives `timeRange` back. The one
 * exception is a bound inside the hour the zone's clocks repeat when they go
 * back: its wall-clock time reads back as the later of the two instants.
 */
export function customBoardTimeRangeOf(
  timeRange: TimeRange,
  calendar: BoardCalendar,
): CustomBoardTimeRange {
  const startWall = wallClockMs(calendar.timeZone, timeRange.start_ms);
  const endWall = wallClockMs(calendar.timeZone, timeRange.end_ms);
  const dateText = (wallMs: number) =>
    new Date(wallMs).toISOString().slice(0, 10);
  return {
    kind: "custom",
    start: isMidnight(startWall)
      ? dateText(startWall)
      : customBoundText(startWall),
    end: isMidnight(endWall)
      ? dateText(endWall - MS_PER_DAY)
      : customBoundText(endWall),
  };
}

function isMidnight(wallMs: number): boolean {
  return ((wallMs % MS_PER_DAY) + MS_PER_DAY) % MS_PER_DAY === 0;
}

/** Whether `text` is a bound a custom range takes: a real date, or a real
 *  date and time of day, with no zone. */
export function isCustomBound(text: string): boolean {
  return customBoundOf(text) !== null;
}

/** A unit in words for an amount: `day` for 1, `days` otherwise. */
export function relativeRangeUnitLabel(
  unit: RelativeRangeUnit,
  amount: number,
): string {
  return amount === 1 ? UNIT_SINGULAR[unit] : unit;
}

/** A board time range in words: a preset's label (`Last 90 days`), a
 *  relative range's amount and unit (`Last 7 days`, `Last 1 day`), or a
 *  custom range's two bounds in the viewer's date and time format
 *  (`boardWallClockText`) and the zone they are read in
 *  (`May 18, 2026 to August 20, 2026 (UTC)`,
 *  `2026-09-06 14:00 to 2026-09-06 14:45 (Pacific Time)` in the ISO format on
 *  a 24-hour clock).
 *  A bound stored with a time of day shows that time even at midnight, since
 *  it does not cover the whole day a bound stored as a date covers. */
export function boardTimeRangeLabel(
  mode: BoardTimeRangeMode,
  calendar: BoardCalendar,
): string {
  if (isPresetBoardTimeRange(mode)) {
    return TIME_RANGES.find((preset) => preset.value === mode)?.label ?? mode;
  }
  if (isRelativeBoardTimeRange(mode)) {
    return `Last ${mode.amount} ${relativeRangeUnitLabel(mode.unit, mode.amount)}`;
  }
  const boundText = (text: string) => {
    const bound = customBoundOf(text);
    return bound === null
      ? text
      : boardWallClockText(bound.wallMs, 0n, { timeAtMidnight: !bound.isDate });
  };
  return `${boundText(mode.start)} to ${boundText(mode.end)} ${boardZoneText(calendar.timeZone)}`;
}

/** The instants from `timeRange`'s start up to its end in words, as a board
 *  writes a custom range over them (`customBoardTimeRangeOf`): a range from
 *  midnight to midnight in the calendar's zone names its first and last days
 *  (`2026-05-04 to 2026-05-10 (UTC)`). */
export function timeRangeLabel(
  timeRange: TimeRange,
  calendar: BoardCalendar,
): string {
  return boardTimeRangeLabel(
    customBoardTimeRangeOf(timeRange, calendar),
    calendar,
  );
}

/** Whether two board time ranges are written the same: the same preset,
 *  custom ranges with the same bounds as written, or relative ranges with the
 *  same amount and unit. `Last 7 days` and `Last 1 week` are written
 *  differently, so they are not the same, and neither are `14:00` and
 *  `14:00:00`. */
export function sameBoardTimeRangeMode(
  first: BoardTimeRangeMode | undefined,
  second: BoardTimeRangeMode | undefined,
): boolean {
  if (first === undefined || second === undefined) {
    return first === second;
  }
  if (isPresetBoardTimeRange(first) || isPresetBoardTimeRange(second)) {
    return first === second;
  }
  if (isRelativeBoardTimeRange(first) || isRelativeBoardTimeRange(second)) {
    return (
      isRelativeBoardTimeRange(first) &&
      isRelativeBoardTimeRange(second) &&
      first.amount === second.amount &&
      first.unit === second.unit
    );
  }
  return first.start === second.start && first.end === second.end;
}
